import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BedDouble, CarTaxiFront, Plane, Sparkles } from "lucide-react";
import { HeroSearch } from "@/components/HeroSearch";
import { PackageCard } from "@/components/PackageCard";
import { OffersStrip } from "@/components/Offers";
import { http } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1526711657229-e7e080ed7aa1?auto=format&fit=crop&w=2000&q=80";

const SERVICES = [
  ["/flights", Plane, "Flights", "Domestic & international fares from IndiGo, Air India, Emirates and more."],
  ["/hotels", BedDouble, "Hotels", "Heritage havelis to overwater villas, priced per night in rupees."],
  ["/cabs", CarTaxiFront, "Taxi", "Airport transfers, local rentals and outstation trips with a driver."],
  ["/planner", Sparkles, "AI Itinerary", "Tell us where and how long — get a day-by-day plan in seconds."],
];

const COPY = {
  domestic: ["Incredible India", "Himalayas to the backwaters", "India"],
  international: ["International holidays", "Beyond borders", "international"],
};

const Destinations = ({ region }) => {
  const { data = [] } = useQuery({ queryKey: ["packages", region], queryFn: () => http.get(`/packages?region=${region}`).then((r) => r.data) });
  const [eyebrow, title, label] = COPY[region];
  return (
    <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-8 lg:pt-28" data-testid={`home-${region}-section`}>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="font-display mt-2 text-4xl font-bold text-[#0D1B2A] sm:text-5xl">{title}</h2>
        </div>
        <Link to={`/packages?region=${region}`} className="inline-flex items-center gap-2 font-semibold text-[#C2603F] hover:gap-3 transition-[gap]" data-testid={`view-all-${region}`}>
          View all {data.length} {label} holidays <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.slice(0, 6).map((p, i) => <PackageCard key={p.id} p={p} i={i} />)}
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <section className="relative min-h-[88vh] overflow-hidden grain">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-900/55 to-slate-900/10" />
        <div className="relative mx-auto flex min-h-[88vh] max-w-7xl flex-col justify-center px-4 py-16 sm:px-8">
          <p className="eyebrow !text-[#F4A261] fade-up">NextVisionSolutions · Tours & Travel</p>
          <h1 className="font-display mt-4 max-w-3xl text-5xl font-bold leading-[1.02] text-white sm:text-6xl lg:text-7xl fade-up" style={{ animationDelay: "80ms" }}>
            Journeys across India <em className="text-[#F4A261]">& the world.</em>
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-200 sm:text-lg fade-up" style={{ animationDelay: "160ms" }}>
            Book flights, hotels, taxis and complete holidays in one place — every price in Indian Rupees.
          </p>
          <div className="mt-10 max-w-5xl fade-up" style={{ animationDelay: "240ms" }}><HeroSearch /></div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-px overflow-hidden rounded-2xl border border-[#E5E0D8] bg-[#E5E0D8] px-0 sm:grid-cols-2 lg:grid-cols-4 mt-20 mx-4 sm:mx-8 xl:mx-auto">
        {SERVICES.map(([to, Icon, t, d], i) => (
          <Link key={to} to={to} data-testid={`service-${t.toLowerCase().replace(/\s/g, "-")}`}
            className="group bg-white p-8 transition-colors hover:bg-[#0D1B2A] fade-up" style={{ animationDelay: `${i * 80}ms` }}>
            <Icon className="h-7 w-7 text-[#E07A5F]" />
            <h3 className="font-display mt-6 text-2xl font-bold text-[#0D1B2A] group-hover:text-white">{t}</h3>
            <p className="mt-2 text-sm text-slate-500 group-hover:text-slate-300">{d}</p>
          </Link>
        ))}
      </section>

      <OffersStrip />
      <Destinations region="domestic" />
      <Destinations region="international" />
      <div className="h-20 lg:h-28" />

      <section className="mx-4 overflow-hidden rounded-3xl bg-[#0D1B2A] sm:mx-8 xl:mx-auto xl:max-w-7xl relative grain">
        <div className="relative grid items-center gap-10 p-10 md:grid-cols-2 lg:p-16">
          <div>
            <p className="eyebrow !text-[#F4A261]">AI travel planner</p>
            <h2 className="font-display mt-3 text-4xl font-bold text-white sm:text-5xl">Your itinerary, written in seconds.</h2>
            <p className="mt-4 max-w-md text-slate-300">Pick a destination, number of days and budget. Our planner drafts a morning-to-evening plan with costs in ₹.</p>
            <Link to="/planner" className="mt-8 inline-flex h-11 items-center gap-2 rounded-full bg-[#E07A5F] px-6 text-sm font-semibold text-white hover:bg-[#C2603F] transition-colors" data-testid="home-planner-cta">
              <Sparkles className="h-4 w-4" />Plan my trip
            </Link>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-slate-300 backdrop-blur">
            <p className="font-display text-2xl font-bold text-white">Day 1: Arrive in Jaipur</p>
            <ul className="mt-3 space-y-2">
              <li><b className="text-[#F4A261]">Morning:</b> Check in to a heritage haveli near MI Road.</li>
              <li><b className="text-[#F4A261]">Afternoon:</b> Hawa Mahal & City Palace.</li>
              <li><b className="text-[#F4A261]">Evening:</b> Dal baati churma at Chokhi Dhani.</li>
              <li><b className="text-[#F4A261]">Est. cost:</b> ₹3,500 per person</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
